import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Chip,
  Divider,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  getJobTranslations,
  regenerateJobTranslations,
} from "../../../api/batches";
import {
  BrandBatchSettings,
  ContentGroupFormatting,
  JobTranslations,
  LanguageCode,
  languageCodeToLabel,
  LARA_TEMPLATE_VARIABLES,
} from "../../../pages/playground/types";
import { getEnabledGroups } from "../../../utils/content-groups";
import PhraseGroup from "../../common/phrase-group";

interface TranslationsDialogProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  masterId?: string;
  availableLanguages?: LanguageCode[];
  isAdmin?: boolean;
  batchSettings?: BrandBatchSettings;
  batchInstructions?: string[];
  batchStyle?: string;
}

export default function TranslationsDialog({
  open,
  onClose,
  jobId,
  masterId,
  availableLanguages,
  isAdmin,
  batchSettings,
  batchInstructions,
  batchStyle,
}: TranslationsDialogProps) {
  const [translations, setTranslations] = useState<JobTranslations | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState<string>("");

  // Regeneration state (admin-only)
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [regenInstructions, setRegenInstructions] = useState("");
  const [regenStyle, setRegenStyle] = useState<string>("faithful");
  const [regenLoading, setRegenLoading] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);

  useEffect(() => {
    if (open && jobId) {
      fetchTranslations();
      // Pre-fill regeneration fields from batch settings
      setRegenInstructions((batchInstructions || []).join("\n"));
      setRegenStyle(batchStyle || "faithful");
    }
  }, [open, jobId]);

  useEffect(() => {
    // Auto-select first available language when translations load
    if (translations && !selectedLanguage) {
      const languages = Object.keys(translations.translations);
      if (languages.length > 0) {
        setSelectedLanguage(languages[0]);
      }
    }
  }, [translations, selectedLanguage]);

  const fetchTranslations = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getJobTranslations(jobId);
      setTranslations(response.data.data);
    } catch (err) {
      setError("Failed to load translations");
      console.error("Error fetching translations:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedLanguage("");
    setTranslations(null);
    setShowRegenerate(false);
    setRegenError(null);
    onClose();
  };

  const handleVariableChipClick = (variableName: string) => {
    const insertText = `{${variableName}}`;
    const newText =
      regenInstructions +
      (regenInstructions && !regenInstructions.endsWith("\n") ? "\n" : "") +
      insertText;
    setRegenInstructions(newText);
  };

  const handleRegenerate = async () => {
    setRegenLoading(true);
    setRegenError(null);
    try {
      const instructions = regenInstructions
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);
      const response = await regenerateJobTranslations(jobId, {
        instructions,
        style: regenStyle || null,
      });
      setTranslations(response.data.data);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Regeneration failed";
      setRegenError(message);
    } finally {
      setRegenLoading(false);
    }
  };

  const getTranslatedLanguages = (): LanguageCode[] => {
    if (!translations) return [];
    return Object.keys(translations.translations) as LanguageCode[];
  };

  const getMissingLanguages = (): LanguageCode[] => {
    if (!availableLanguages) return [];
    const translated = getTranslatedLanguages();
    return availableLanguages.filter((lang) => !translated.includes(lang));
  };

  const getAllLanguages = (): {
    lang: LanguageCode;
    hasTranslation: boolean;
  }[] => {
    const translated = getTranslatedLanguages();
    const missing = getMissingLanguages();
    return [
      ...translated.map((lang) => ({ lang, hasTranslation: true })),
      ...missing.map((lang) => ({ lang, hasTranslation: false })),
    ];
  };

  const renderTranslationContent = () => {
    if (!translations || !selectedLanguage) return null;

    const languageTranslations =
      translations.translations[selectedLanguage as LanguageCode];
    if (!languageTranslations) return null;

    const sourceLanguage = translations.source_language;
    const originals = translations.originals || {};

    // Build an ordered list of "sections" to render, respecting the
    // content_groups sortOrder set by the user and merging groups per
    // export_rules (identical to the CSV export logic).
    const enabledGroups = getEnabledGroups(batchSettings);
    const exportRules = batchSettings?.export_rules || [];
    const mergedGroupIds = new Set(
      exportRules.flatMap((r) => r.sourceGroups),
    );

    type SectionItem = {
      formatting: ContentGroupFormatting;
      originals: string[];
      translations: string[];
    };
    type Section = {
      kind: "group" | "rule";
      key: string;
      label: string;
      sortOrder: number;
      items: SectionItem[];
    };

    const sections: Section[] = [];

    // Individual group sections (skip those consumed by a rule)
    for (const group of enabledGroups) {
      if (mergedGroupIds.has(group.id)) continue;
      const groupTranslations = languageTranslations[group.id];
      const groupOriginals = originals[group.id] || [];
      // Skip if nothing to show
      if (
        (!groupTranslations || groupTranslations.length === 0) &&
        groupOriginals.length === 0
      ) {
        continue;
      }
      sections.push({
        kind: "group",
        key: group.id,
        label: group.label,
        sortOrder: group.sortOrder,
        items: [
          {
            formatting: group.formatting,
            originals: groupOriginals,
            translations: groupTranslations || [],
          },
        ],
      });
    }

    // Export rule sections: one section per rule, with a sub-block per
    // source group (each keeping its own formatting — mirrors the Python
    // backend which concatenates formatted strings with "\n\n").
    for (const rule of exportRules) {
      if (!rule.label || rule.sourceGroups.length === 0) continue;
      const items = rule.sourceGroups
        .map((gid) => {
          const g = enabledGroups.find((eg) => eg.id === gid);
          if (!g) return null;
          const t = languageTranslations[g.id] || [];
          const o = originals[g.id] || [];
          if (t.length === 0 && o.length === 0) return null;
          return {
            formatting: g.formatting,
            originals: o,
            translations: t,
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);

      if (items.length === 0) continue;

      // Sort order = min sortOrder among source groups (rule appears where
      // its earliest source group would)
      const minOrder = Math.min(
        ...rule.sourceGroups.map(
          (gid) => enabledGroups.find((g) => g.id === gid)?.sortOrder ?? 999,
        ),
      );

      sections.push({
        kind: "rule",
        key: `rule:${rule.id}`,
        label: rule.label,
        sortOrder: minOrder,
        items,
      });
    }

    // Sort all sections by their sortOrder (user-configured order)
    sections.sort((a, b) => a.sortOrder - b.sortOrder);

    return (
      <Box sx={{ mt: 2 }}>
        {/* Column Headers */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                fontSize: 11,
                letterSpacing: 0.5,
              }}
            >
              Original
              {sourceLanguage
                ? ` (${languageCodeToLabel(sourceLanguage)})`
                : ""}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                fontSize: 11,
                letterSpacing: 0.5,
              }}
            >
              Translation (
              {languageCodeToLabel(selectedLanguage as LanguageCode)})
            </Typography>
          </Box>
        </Box>

        {sections.map((section, index) => (
          <Box key={section.key} sx={{ mb: 3 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                textTransform: "uppercase",
                fontSize: 11,
                letterSpacing: 0.5,
                mb: 1,
              }}
            >
              {section.label}
            </Typography>
            {section.items.map((item, itemIdx) => (
              <Box
                key={itemIdx}
                sx={{
                  display: "flex",
                  gap: 2,
                  alignItems: "flex-start",
                  mt: itemIdx > 0 ? 1.5 : 0,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "grey.100",
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <PhraseGroup
                    phrases={item.originals}
                    formatting={item.formatting}
                    mode="rendered"
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    backgroundColor: "grey.50",
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.200",
                  }}
                >
                  <PhraseGroup
                    phrases={item.translations}
                    formatting={item.formatting}
                    mode="rendered"
                  />
                </Box>
              </Box>
            ))}
            {index < sections.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: 400 },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
        }}
      >
        <Box>
          <Typography variant="h6" component="span">
            Translations
          </Typography>
          {masterId && (
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              sx={{ ml: 1 }}
            >
              SKU: {masterId}
            </Typography>
          )}
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 200,
            }}
          >
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && translations && (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
              }}
            >
              {getAllLanguages().map(({ lang, hasTranslation }) => (
                <Chip
                  key={lang}
                  label={languageCodeToLabel(lang as LanguageCode)}
                  onClick={
                    hasTranslation ? () => setSelectedLanguage(lang) : undefined
                  }
                  color={selectedLanguage === lang ? "secondary" : "default"}
                  variant={selectedLanguage === lang ? "filled" : "outlined"}
                  disabled={!hasTranslation}
                  sx={{
                    cursor: hasTranslation ? "pointer" : "default",
                    opacity: hasTranslation ? 1 : 0.5,
                  }}
                />
              ))}
              {/* Regenerate button hidden — may be re-enabled in the future
              {isAdmin && (
                <Chip
                  label="Regenerate"
                  size="small"
                  color={showRegenerate ? "secondary" : "default"}
                  variant={showRegenerate ? "filled" : "outlined"}
                  onClick={() => setShowRegenerate(!showRegenerate)}
                  sx={{ cursor: "pointer" }}
                />
              )}
              */}
            </Box>

            {isAdmin && showRegenerate && (
              <Paper
                variant="outlined"
                sx={{ p: 2, mb: 2, backgroundColor: "grey.50" }}
              >
                {masterId && (
                  <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500 }}>
                    SKU: <code>{masterId}</code>
                  </Typography>
                )}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Instructions (one per line)
                </Typography>
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 0.5, display: "block" }}
                  >
                    Available bullet points (click to insert):
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {LARA_TEMPLATE_VARIABLES.map((variable) => (
                      <Chip
                        key={variable.name}
                        label={`{${variable.name}}`}
                        size="small"
                        variant="outlined"
                        onClick={() => handleVariableChipClick(variable.name)}
                        sx={{
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: "action.hover",
                          },
                        }}
                        title={variable.description}
                      />
                    ))}
                  </Box>
                </Box>
                <TextField
                  multiline
                  minRows={3}
                  maxRows={8}
                  fullWidth
                  size="small"
                  value={regenInstructions}
                  onChange={(e) => setRegenInstructions(e.target.value)}
                  sx={{
                    mb: 2,
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                      fontSize: 13,
                    },
                  }}
                />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Style
                </Typography>
                <RadioGroup
                  row
                  value={regenStyle}
                  onChange={(e) => setRegenStyle(e.target.value)}
                  sx={{ mb: 2 }}
                >
                  <FormControlLabel
                    value="faithful"
                    control={<Radio size="small" />}
                    label="Faithful"
                  />
                  <FormControlLabel
                    value="fluid"
                    control={<Radio size="small" />}
                    label="Fluid"
                  />
                  <FormControlLabel
                    value="creative"
                    control={<Radio size="small" />}
                    label="Creative"
                  />
                </RadioGroup>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleRegenerate}
                    disabled={regenLoading}
                  >
                    {regenLoading ? "Regenerating..." : "Regenerate"}
                  </Button>
                  {regenLoading && <CircularProgress size={20} />}
                  {regenError && (
                    <Typography variant="body2" color="error">
                      {regenError}
                    </Typography>
                  )}
                </Box>
              </Paper>
            )}

            {renderTranslationContent()}
          </>
        )}

        {!loading &&
          !error &&
          translations &&
          Object.keys(translations.translations).length === 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 200,
                gap: 1,
              }}
            >
              <Typography color="text.secondary">
                No translations available yet
              </Typography>
              {availableLanguages && availableLanguages.length > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Missing:{" "}
                  {availableLanguages
                    .map((l) => languageCodeToLabel(l))
                    .join(", ")}
                </Typography>
              )}
            </Box>
          )}
      </DialogContent>
    </Dialog>
  );
}
